import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Upload, Plus, ShieldCheck, ExternalLink, Search, PenLine, Briefcase, X, Linkedin } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { LogReplyDialog } from "@/components/LogReplyDialog";
import { ContactDetail } from "@/components/ContactDetail";
import { FieldHelp, LabelHelp, PageTip } from "@/components/FieldHelp";
import { HELP } from "@/lib/fieldHelp";
import { STAGE_LABEL } from "@/lib/constants";
import api from "@/lib/api";

const SOURCES = ["apollo", "public_dir", "csv", "nav_export", "manual", "samgov", "usaspending", "sandbox_public"];
const STAGES = ["lead", "enriched", "sequenced", "replied", "meeting_booked", "qualified", "proposal", "won", "lost"];
const ALL = "__all__";

const SOURCE_BADGE = {
  apollo: "text-sky-500 bg-sky-500/10",
  public_dir: "text-violet-400 bg-violet-500/10",
  csv: "text-amber-500 bg-amber-500/10",
  nav_export: "text-emerald-500 bg-emerald-500/10",
  manual: "text-muted-foreground bg-muted",
  samgov: "text-sky-400 bg-sky-500/10",
  usaspending: "text-teal-400 bg-teal-500/10",
  sandbox_public: "text-indigo-400 bg-indigo-500/10",
};
const PUBLIC_SOURCES = ["samgov", "usaspending", "public_dir", "sandbox_public"];

function scoreColor(s) { return s >= 80 ? "text-emerald-500" : s >= 60 ? "text-amber-500" : "text-muted-foreground"; }

export default function Leads() {
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState(searchParams.get("focus") || "");
  const [importOpen, setImportOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [csv, setCsv] = useState("name,title,company,email,source_url\nJordan Reyes,VP Sales,Example Corp,jordan@example.test,");
  const [navCsv, setNavCsv] = useState("name,title,company,linkedin_url,email\nAvery Chen,Head of Procurement,Northwind Civic,https://linkedin.com/in/averychen,");
  const [add, setAdd] = useState({ name: "", title: "", company: "", email: "", source: "manual", source_url: "" });
  const [logContact, setLogContact] = useState(null);
  const [logLabel, setLogLabel] = useState("");
  const [filters, setFilters] = useState({ source: ALL, stage: ALL, sequence: ALL, min_score: ALL, sector: ALL });
  const [detailId, setDetailId] = useState(null);
  const [flags, setFlags] = useState({ navigator_import: true });

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (filters.source !== ALL) params.source = filters.source;
    if (filters.sector === "public") params.sector = "public";
    if (filters.sector === "private") params.sector = "private";
    if (filters.stage !== ALL) params.stage = filters.stage;
    if (filters.sequence === "active") params.sequence = "active";
    if (filters.min_score !== ALL) params.min_score = filters.min_score;
    const { data } = await api.get("/contacts", { params });
    setContacts(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, filters]);
  const resetFilters = () => setFilters({ source: ALL, stage: ALL, sequence: ALL, min_score: ALL, sector: ALL });
  const filtersActive = q || Object.values(filters).some((v) => v !== ALL);

  const doImport = async () => {
    const lines = csv.trim().split("\n");
    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const cells = line.split(",");
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
      return obj;
    });
    try {
      const { data } = await api.post("/contacts/import", { rows, source: "csv" });
      toast.success(`Imported ${data.imported} · ${data.skipped_duplicates} dupes skipped${data.blocked_by_quota ? ` · ${data.blocked_by_quota} over quota` : ""}`);
      setImportOpen(false);
      load();
    } catch { toast.error("Import failed"); }
  };

  const doNavImport = async () => {
    const lines = navCsv.trim().split("\n");
    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const cells = line.split(",");
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
      return obj;
    });
    try {
      const { data } = await api.post("/contacts/import", { rows, source: "nav_export" });
      toast.success(`Imported ${data.imported} from Navigator. Contacts without a work email are queued for manual LinkedIn outreach.`);
      setNavOpen(false);
      load();
    } catch { toast.error("Navigator import failed"); }
  };

  const addToQueue = async (c) => {
    try { await api.post("/linkedin-tasks", { contact_id: c.id }); toast.success(`${c.name} added to the LinkedIn task queue`); }
    catch { toast.error("Could not add to queue"); }
  };

  const doAdd = async () => {    if (!add.name) return toast.error("Name required");
    await api.post("/contacts", add);
    setAddOpen(false);
    setAdd({ name: "", title: "", company: "", email: "", source: "manual", source_url: "" });
    load();
    toast.success("Contact added");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/org/status").then((r) => setFlags(r.data.feature_flags || { navigator_import: true })).catch(() => {}); }, []);

  const createDeal = async (c) => {
    try {
      await api.post("/opportunities", { name: c.name, company: c.company, contact_email: c.email, contact_id: c.id, stage: c.stage || "lead" });
      toast.success("Deal added to the pipeline");
    } catch { toast.error("Could not create deal"); }
  };

  return (
    <PageBody>
      <PageHeader eyebrow="Lead Database" title="Verified, licensed contacts"
        subtitle="Every record carries a source. Public-sector rows store a source URL. Unknown emails can't auto-send."
        actions={
          <div className="flex gap-2">
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild><Button variant="outline" className="rounded-md" data-testid="add-contact-btn"><Plus className="h-4 w-4 mr-1.5" /> Add</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Add a contact</DialogTitle></DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} className="mt-1.5" data-testid="add-name" /></div>
                  <div><Label>Title</Label><Input value={add.title} onChange={(e) => setAdd({ ...add, title: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Company</Label><Input value={add.company} onChange={(e) => setAdd({ ...add, company: e.target.value })} className="mt-1.5" /></div>
                  <div><LabelHelp label="Email" help={HELP.lead_email} testid="help-lead-email" /><Input value={add.email} onChange={(e) => setAdd({ ...add, email: e.target.value })} className="mt-1.5" data-testid="add-email" placeholder="dana@company.com" /></div>
                  <div className="sm:col-span-2"><Label>Source URL (public records)</Label><Input value={add.source_url} onChange={(e) => setAdd({ ...add, source_url: e.target.value, source: e.target.value ? "public_dir" : "manual" })} placeholder="https://sam.gov/opp/…" className="mt-1.5" /></div>
                </div>
                <DialogFooter><Button onClick={doAdd} className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[44px]" data-testid="save-contact-btn">Add contact</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={navOpen} onOpenChange={setNavOpen}>
              <DialogTrigger asChild><Button variant="outline" className={`rounded-md ${flags.navigator_import ? "" : "hidden"}`} data-testid="import-nav-btn"><Linkedin className="h-4 w-4 mr-1.5 text-sky-500" /> Import Navigator CSV</Button></DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="flex items-center gap-1.5"><Linkedin className="h-4 w-4 text-sky-500" /> Import Sales Navigator CSV</DialogTitle><DialogDescription>Bring your own Navigator license. DealsOps never scrapes LinkedIn or sends DMs. Columns: name, title, company, linkedin_url, email (optional). Source is tagged nav_export. Rows without a work email become unverified and land on the LinkedIn task queue - they cannot auto-send.</DialogDescription></DialogHeader>
                <Textarea value={navCsv} onChange={(e) => setNavCsv(e.target.value)} rows={8} className="font-mono text-xs" data-testid="nav-csv-input" />
                <DialogFooter><Button onClick={doNavImport} className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[44px]" data-testid="run-nav-import-btn">Import from Navigator</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild><Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="import-csv-btn"><Upload className="h-4 w-4 mr-1.5" /> Import CSV</Button></DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="flex items-center gap-1.5">Import contacts (CSV) <FieldHelp title="Lead Finder & import" testid="help-lead-import" {...HELP.lead_import} /></DialogTitle><DialogDescription>Work emails only - no home addresses. Columns: name, title, company, work email, LinkedIn URL. Dedupes on email + LinkedIn + name/company and respects your contact quota.</DialogDescription></DialogHeader>
                <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} className="font-mono text-xs" data-testid="csv-input" />
                <DialogFooter><Button onClick={doImport} className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[44px]" data-testid="run-import-btn">Import</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <PageTip id="leads">
        This list is people who match your ICP and can be emailed at work. Import a CSV (name, title, company, work email, LinkedIn URL) - work emails only.
      </PageTip>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email…" className="pl-9" data-testid="leads-search" />
        </div>
        <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
          <SelectTrigger className="w-[140px]" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All sources</SelectItem>{SOURCES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.stage} onValueChange={(v) => setFilters({ ...filters, stage: v })}>
          <SelectTrigger className="w-[140px]" data-testid="filter-stage"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All stages</SelectItem>{STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.sequence} onValueChange={(v) => setFilters({ ...filters, sequence: v })}>
          <SelectTrigger className="w-[150px]" data-testid="filter-sequence"><SelectValue placeholder="Sequence" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Any sequence</SelectItem><SelectItem value="active">In sequence</SelectItem></SelectContent>
        </Select>
        <Select value={filters.sector} onValueChange={(v) => setFilters({ ...filters, sector: v })}>
          <SelectTrigger className="w-[150px]" data-testid="filter-sector"><SelectValue placeholder="Sector" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All sectors</SelectItem><SelectItem value="private">Private</SelectItem><SelectItem value="public">Public sector</SelectItem></SelectContent>
        </Select>
        <Select value={filters.min_score} onValueChange={(v) => setFilters({ ...filters, min_score: v })}>
          <SelectTrigger className="w-[130px]" data-testid="filter-score"><SelectValue placeholder="Min score" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Any score</SelectItem><SelectItem value="80">80+</SelectItem><SelectItem value="60">60+</SelectItem><SelectItem value="40">40+</SelectItem></SelectContent>
        </Select>
        {filtersActive && <Button variant="ghost" size="sm" className="h-9" onClick={() => { setQ(""); resetFilters(); }} data-testid="clear-filters"><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>}
      </div>

      {contacts.length === 0 ? (
        <EmptyState icon={Users} title="No contacts in this workspace" body="Import a CSV or add contacts to start building your list." />
      ) : (
        <div className="rounded-xl border border-border/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-border/70 bg-card/50 text-xs text-muted-foreground">
                  <th className="text-left font-medium p-3 pl-4">Contact</th>
                  <th className="text-left font-medium p-3">Company</th>
                  <th className="text-left font-medium p-3">Source</th>
                  <th className="text-left font-medium p-3">Email</th>
                  <th className="text-center font-medium p-3">Fit</th>
                  <th className="text-left font-medium p-3">Stage</th>
                  <th className="text-right font-medium p-3 pr-4"></th>
                </tr>
              </thead>
              <tbody data-testid="leads-table">
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-border/40 last:border-0 hover:bg-accent/30" data-testid={`lead-row-${c.id}`}>
                    <td className="p-3 pl-4">
                      <button className="text-left group" onClick={() => setDetailId(c.id)} data-testid={`open-contact-${c.id}`}>
                        <div className="font-medium group-hover:text-amber-500 transition-colors flex items-center gap-1.5">{c.name}{c.in_sequence && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="In sequence" />}</div>
                        <div className="text-xs text-muted-foreground">{c.title}</div>
                      </button>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      <div>{c.company}</div>
                      {c.agency && <div className="text-[11px] text-sky-500 flex items-center gap-1" data-testid={`lead-agency-${c.id}`}><ShieldCheck className="h-3 w-3" /> {c.agency}{c.role ? ` · ${c.role}` : ""}</div>}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${SOURCE_BADGE[c.source] || SOURCE_BADGE.manual}`}>{c.source}</span>
                        {c.source_url
                          ? <a href={c.source_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-amber-500 inline-flex items-center gap-0.5 text-[10px]" title={c.source_url} data-testid={`lead-source-url-${c.id}`}><ExternalLink className="h-3 w-3" /> Source</a>
                          : PUBLIC_SOURCES.includes(c.source) && <span className="text-[10px] text-rose-500" title="Public contacts need a source URL to send">no source</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-mono text-muted-foreground truncate max-w-[160px]">{c.email || "-"}</span>
                        {c.email_status === "verified" && <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                        {c.email_status === "unknown" && <span className="text-[10px] text-rose-500">no-send</span>}
                        {c.email_status === "unverified" && <span className="text-[10px] text-amber-500">unverified</span>}
                      </div>
                    </td>
                    <td className={`p-3 text-center metric ${scoreColor(c.fit_score)}`}>{c.fit_score}</td>
                    <td className="p-3"><Badge variant="outline" className="text-xs capitalize">{c.stage}</Badge></td>
                    <td className="p-3 pr-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.linkedin_url && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-sky-500" onClick={() => addToQueue(c)} data-testid={`queue-lead-${c.id}`}>
                            <Linkedin className="h-3.5 w-3.5 mr-1" /> Queue
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => createDeal(c)} data-testid={`create-deal-lead-${c.id}`}>
                          <Briefcase className="h-3.5 w-3.5 mr-1" /> Deal
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setLogLabel(`${c.name} · ${c.company}`); setLogContact(c.id); }} data-testid={`log-reply-lead-${c.id}`}>
                          <PenLine className="h-3.5 w-3.5 mr-1" /> Log reply
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <LogReplyDialog open={!!logContact} onOpenChange={(v) => !v && setLogContact(null)} presetContactId={logContact || ""} presetContactLabel={logLabel} onSaved={() => { setLogContact(null); load(); }} />
      <ContactDetail contactId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} />
    </PageBody>
  );
}
